// Note: Block name utilities are exported from propai/utils to avoid conflicts
// These have different format requirements for different data sources

// Note: extractHeatEnergySum is exported from propai/utils to avoid conflicts

const UNIT_LADDER = ["kWh", "MWh", "GWh", "TWh"] as const;
type Unit = (typeof UNIT_LADDER)[number];

function unitIndex(u: string): number {
  const i = UNIT_LADDER.indexOf(u as Unit);
  return i === -1 ? 0 : i;
}

export function oneBelowUnit(axisUnit: string): Unit {
  const i = unitIndex(axisUnit);
  return UNIT_LADDER[Math.max(0, i - 1)];
}

export function divisorForUnit(u: string): number {
  switch (u) {
    case "TWh":
      return 1e9; // using kWh as the reference unit
    case "GWh":
      return 1e6;
    case "MWh":
      return 1e3;
    case "kWh":
    default:
      return 1;
  }
}

export function axisUnitForMaxKwh(maxKwh: number): Unit {
  if (maxKwh >= 1e9) return "TWh";
  if (maxKwh >= 1e6) return "GWh";
  if (maxKwh >= 1e3) return "MWh";
  return "kWh";
}

/**
 * Resolves the axis unit honoring an optional energy-unit-prefix override.
 * When `energyUnitPrefix === "k"`, axis unit is locked to "kWh".
 */
export function resolveAxisUnit(maxKwh: number, energyUnitPrefix?: "" | "k"): Unit {
  if (energyUnitPrefix === "k") return "kWh";
  return axisUnitForMaxKwh(maxKwh);
}

/**
 * Format meter component data for charts
 */
export const formatMeterComponentData = (component: any) => {
  if (!component || !component.timestamps || !component.values) {
    return null;
  }

  const formattedData: any[] = [];

  component.timestamps.forEach((timestamp: string, index: number) => {
    if (component.values[index] !== null && component.values[index] !== undefined) {
      formattedData.push({
        timestamp,
        value: component.values[index],
        componentType: component.type || "unknown",
      });
    }
  });

  return formattedData;
};

/**
 * Combine multiple formatted data arrays
 */
export const combineFormattedData = (...dataArrays: any[]): any[] => {
  const combined: any[] = [];

  dataArrays.forEach((dataArray) => {
    if (Array.isArray(dataArray)) {
      combined.push(...dataArray);
    }
  });

  // Sort by timestamp
  return combined.sort((a, b) => {
    const dateA = new Date(a.timestamp).getTime();
    const dateB = new Date(b.timestamp).getTime();
    return dateA - dateB;
  });
};
